/**
 * GoogleSignInButton Component - Phase 1.7
 *
 * Branded Google Sign-in button following Google's design guidelines.
 * Uses official Google "G" logo SVG per brand guidelines.
 *
 * Architecture: Uses Supabase OAuth with expo-web-browser
 * Created: 2025-01-03
 * Updated: 2025-01-12 (Added official Google logo)
 */

import React, { useState } from 'react';
import { TouchableOpacity, Text, View, ActivityIndicator } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useAuth } from '../contexts/AuthContext';
import { captureEvent } from '../lib/analytics';

/**
 * GoogleLogo Component
 * Official Google "G" logo following Google's brand guidelines
 * Colors: #4285F4 (blue), #34A853 (green), #FBBC05 (yellow), #EA4335 (red)
 */
function GoogleLogo() {
  return (
    <Svg width="20" height="20" viewBox="0 0 48 48">
      {/* Blue */}
      <Path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      {/* Green */}
      <Path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      {/* Yellow */}
      <Path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
      />
      {/* Red */}
      <Path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </Svg>
  );
}

interface GoogleSignInButtonProps {
  onPress?: () => void;
  disabled?: boolean;
}

export function GoogleSignInButton({
  onPress,
  disabled = false,
}: GoogleSignInButtonProps) {
  const { signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);

  const handlePress = async () => {
    try {
      setLoading(true);
      captureEvent('google_signin_button_clicked');

      if (onPress) {
        onPress();
      } else {
        await signInWithGoogle();
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={disabled || loading}
      className="flex-row items-center justify-center bg-white border border-gray-300 rounded-lg px-6 py-3 shadow-sm active:bg-gray-50"
    >
      {loading ? (
        <ActivityIndicator size="small" color="#4285F4" />
      ) : (
        <>
          {/* Official Google "G" Logo */}
          <View className="mr-3">
            <GoogleLogo />
          </View>

          <Text className="text-gray-700 font-medium text-base">
            Continue with Google
          </Text>
        </>
      )}
    </TouchableOpacity>
  );
}
